import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Search, X, type LucideIcon } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { fetchBookings } from '@store/slices/bookingSlice';
import { fetchConversations } from '@store/slices/conversationSlice';
import { setOpenContactTarget } from '@store/slices/contactNavigationSlice';
import { setProjectNavTarget } from '@store/slices/projectNavigationSlice';
import { fetchContacts } from '@store/slices/contactSlice';
import { fetchScheduledEmails as fetchScheduledEmailsThunk } from '@store/slices/emailSlice';
import { fetchInvoices } from '@store/slices/invoiceSlice';
import { fetchPackages } from '@store/slices/packageSlice';
import { fetchProjects } from '@store/slices/projectSlice';
import { fetchSupportFeedback } from '@store/slices/supportFeedbackSlice';
import { getGlobalSearchItems } from '@components/dashboard/dashboardNavigation';
import { getGlobalSearchShortcutLabel } from '@components/dashboard/globalSearchShortcut';
import {
  GLOBAL_SEARCH_SCOPES,
  GLOBAL_SEARCH_SCOPE_LABELS,
  isGlobalSearchScope,
  parseScopeQuery,
  type GlobalSearchScope,
} from '@components/search/parseScopeQuery';
import { FAQ_DATA } from '@drahft/utils/help/faqData';
import { APP_ROUTES } from '@config/routes';
import { useModal } from '@/hooks/useModal';
import { StandardModal } from '@/modals/StandardModal';
import { cn } from '@/utils/cn';

type SearchResult = {
  id: string;
  title: string;
  description: string;
  section: string;
  keywords: string[];
  icon: LucideIcon;
  route: string;
  badge?: string;
  current?: boolean;
  action?: () => void;
};

const GlobalSearchModal = () => {
  const dispatch = useAppDispatch();
  const modal = useModal('global.search');
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [scopeOverride, setScopeOverride] = useState<GlobalSearchScope | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const contacts = useAppSelector(state => state.contacts.contacts);
  const projects = useAppSelector(state => state.projects.projects);
  const bookings = useAppSelector(state => state.bookings.bookings);
  const conversations = useAppSelector(state => state.conversations.conversations);
  const invoices = useAppSelector(state => state.invoices.invoices);
  const packages = useAppSelector(state => state.packages.packages);
  const feedbackItems = useAppSelector(state => state.supportFeedback.items);
  const routeItems = useMemo(() => getGlobalSearchItems(), []);
  const currentRoute = `${location.pathname}${location.search}`;
  const payloadScope =
    typeof modal.payload?.scope === 'string' && isGlobalSearchScope(modal.payload.scope)
      ? modal.payload.scope
      : null;
  const parsedQuery = useMemo(
    () => parseScopeQuery(query, scopeOverride ?? payloadScope),
    [payloadScope, query, scopeOverride]
  );
  const normalizedQuery = parsedQuery.query.toLowerCase();
  const activeScopes = parsedQuery.scopes;

  const scopeMatches = useCallback(
    (scope: GlobalSearchScope) => activeScopes.length === 0 || activeScopes.includes(scope),
    [activeScopes]
  );

  const routeResults = useMemo<SearchResult[]>(
    () =>
      routeItems.map(item => ({
        id: `route:${item.href}`,
        title: item.name,
        description: item.description,
        section: item.section,
        keywords: item.keywords,
        icon: item.icon,
        route: item.href,
        current: currentRoute === item.href,
      })),
    [currentRoute, routeItems]
  );

  const contactResults = useMemo<SearchResult[]>(() => {
    if (!normalizedQuery || !scopeMatches('contacts')) {
      return [];
    }

    return contacts
      .filter(contact => {
        const haystack = [contact.name, contact.email, contact.phone ?? '', contact.status].join(
          ' '
        );
        return haystack.toLowerCase().includes(normalizedQuery);
      })
      .slice(0, 5)
      .map(contact => ({
        id: `contact:${contact.id}`,
        title: contact.name,
        description: contact.email || contact.phone || 'Open contact record',
        section: 'Clients',
        keywords: [contact.email, contact.phone ?? '', contact.status],
        icon: Search,
        route: '/dashboard/clients',
        badge: contact.status,
        action: () => {
          dispatch(
            setOpenContactTarget({
              contactId: contact.id,
              email: contact.email,
            })
          );
          navigate('/dashboard/clients');
          modal.close();
        },
      }));
  }, [contacts, dispatch, modal, navigate, normalizedQuery, scopeMatches]);

  const projectResults = useMemo<SearchResult[]>(() => {
    if (!normalizedQuery || !scopeMatches('projects')) {
      return [];
    }

    return projects
      .filter(project => {
        const haystack = [
          project.name,
          project.contactName,
          project.status,
          project.workflowStatus,
          project.contractStatus,
          project.projectSource ?? '',
        ].join(' ');
        return haystack.toLowerCase().includes(normalizedQuery);
      })
      .slice(0, 5)
      .map(project => ({
        id: `project:${project.id}`,
        title: project.name,
        description: project.contactName || 'Open project details',
        section: 'Projects',
        keywords: [project.status, project.workflowStatus, project.contractStatus],
        icon: Search,
        route: `/dashboard/projects?q=${encodeURIComponent(project.name)}&projectId=${encodeURIComponent(project.id)}`,
        badge: project.status,
      }));
  }, [normalizedQuery, projects, scopeMatches]);

  const bookingResults = useMemo<SearchResult[]>(() => {
    if (!normalizedQuery || !scopeMatches('bookings')) {
      return [];
    }

    return bookings
      .filter(booking => {
        const haystack = [
          booking.summary,
          booking.contactName,
          booking.contactEmail,
          booking.status,
          booking.bookingType ?? '',
        ].join(' ');
        return haystack.toLowerCase().includes(normalizedQuery);
      })
      .slice(0, 5)
      .map(booking => {
        const tab = booking.bookingType === 'meeting' ? 'meetings' : 'events';
        return {
          id: `booking:${booking.id}`,
          title: booking.summary,
          description: `${booking.contactName} · ${booking.contactEmail}`,
          section: 'Bookings',
          keywords: [booking.status, booking.bookingType ?? tab],
          icon: Search,
          route: `/dashboard/bookings?tab=${tab}&q=${encodeURIComponent(booking.summary)}`,
          badge: booking.status,
        };
      });
  }, [bookings, normalizedQuery, scopeMatches]);

  const emailResults = useMemo<SearchResult[]>(() => {
    if (!normalizedQuery || !scopeMatches('emails')) {
      return [];
    }

    return conversations
      .filter(conversation => {
        const latestMessage = conversation.messages[conversation.messages.length - 1];
        const haystack = [
          conversation.metadata?.lastEmailSubject ?? '',
          conversation.metadata?.contactName ?? conversation.metadata?.name ?? '',
          conversation.metadata?.contactEmail ?? conversation.metadata?.email ?? '',
          latestMessage?.content ?? '',
          conversation.status,
        ].join(' ');
        return haystack.toLowerCase().includes(normalizedQuery);
      })
      .slice(0, 5)
      .map(conversation => ({
        id: `email:${conversation.id}`,
        title:
          conversation.metadata?.lastEmailSubject ??
          conversation.metadata?.contactName ??
          conversation.metadata?.name ??
          'Untitled thread',
        description:
          conversation.metadata?.contactEmail ??
          conversation.metadata?.email ??
          'Open conversation',
        section: 'Messages',
        keywords: [conversation.status],
        icon: Search,
        route: `/dashboard/email?tab=inbox&id=${encodeURIComponent(conversation.id)}`,
        badge: conversation.status,
      }));
  }, [conversations, normalizedQuery, scopeMatches]);

  const invoiceResults = useMemo<SearchResult[]>(() => {
    if (!normalizedQuery || !scopeMatches('invoices')) {
      return [];
    }

    return invoices
      .filter(invoice => {
        const haystack = [
          invoice.invoiceNumber,
          invoice.projectName,
          invoice.contactName,
          invoice.contactEmail,
          invoice.status,
        ].join(' ');
        return haystack.toLowerCase().includes(normalizedQuery);
      })
      .slice(0, 5)
      .map(invoice => ({
        id: `invoice:${invoice.id}`,
        title: invoice.invoiceNumber,
        description: invoice.projectName || invoice.contactName || 'Open invoice',
        section: 'Invoices',
        keywords: [invoice.status, invoice.contactEmail],
        icon: Search,
        route: APP_ROUTES.PROJECTS,
        badge: invoice.status,
        action: () => {
          dispatch(
            setProjectNavTarget({
              projectId: invoice.projectId,
              initialTab: 'invoicing',
              invoiceId: invoice.id,
            })
          );
          navigate(APP_ROUTES.PROJECTS);
          modal.close();
        },
      }));
  }, [dispatch, invoices, modal, navigate, normalizedQuery, scopeMatches]);

  const packageResults = useMemo<SearchResult[]>(() => {
    if (!normalizedQuery || !scopeMatches('packages')) {
      return [];
    }

    return packages
      .filter(pkg => {
        const haystack = [
          pkg.name,
          ...pkg.items.map(item => `${item.name} ${item.category ?? ''}`),
        ].join(' ');
        return haystack.toLowerCase().includes(normalizedQuery);
      })
      .slice(0, 5)
      .map(pkg => ({
        id: `package:${pkg.id}`,
        title: pkg.name,
        description: `${pkg.items.length} items`,
        section: 'Packages',
        keywords: pkg.items.map(item => item.name),
        icon: Search,
        route: `${APP_ROUTES.TEMPLATES}?tab=packages`,
      }));
  }, [normalizedQuery, packages, scopeMatches]);

  const feedbackResults = useMemo<SearchResult[]>(() => {
    if (!normalizedQuery || !scopeMatches('feedback')) {
      return [];
    }

    return feedbackItems
      .filter(item => {
        const haystack = [
          item.subject,
          item.userName,
          item.userEmail,
          item.message,
          item.status,
          item.category,
        ].join(' ');
        return haystack.toLowerCase().includes(normalizedQuery);
      })
      .slice(0, 5)
      .map(item => ({
        id: `feedback:${item.id}`,
        title: item.subject,
        description: item.userEmail || item.userName || 'Open support ticket',
        section: 'Support',
        keywords: [item.status, item.category],
        icon: Search,
        route: '/dashboard/feedback',
        badge: item.status,
      }));
  }, [feedbackItems, normalizedQuery, scopeMatches]);

  const faqResults = useMemo<SearchResult[]>(() => {
    if (!normalizedQuery || !scopeMatches('help')) {
      return [];
    }

    return FAQ_DATA.flatMap(category =>
      category.items.map((item, index) => ({ category, item, index }))
    )
      .filter(({ category, item }) => {
        const haystack = [category.title, item.question, item.answer].join(' ');
        return haystack.toLowerCase().includes(normalizedQuery);
      })
      .slice(0, 5)
      .map(({ category, item, index }) => ({
        id: `help:${category.title}:${index}`,
        title: item.question,
        description: category.title,
        section: 'Help Center',
        keywords: [category.title],
        icon: Search,
        route: `${APP_ROUTES.HELP}?q=${encodeURIComponent(item.question)}`,
      }));
  }, [normalizedQuery, scopeMatches]);

  const filteredItems = useMemo(() => {
    if (!normalizedQuery) {
      return scopeMatches('routes') ? routeResults : [];
    }

    const matchedRoutes = scopeMatches('routes')
      ? routeResults.filter(item => {
          const haystack = [item.title, item.description, item.section, ...item.keywords].join(' ');
          return haystack.toLowerCase().includes(normalizedQuery);
        })
      : [];

    return [
      ...matchedRoutes,
      ...faqResults,
      ...contactResults,
      ...projectResults,
      ...bookingResults,
      ...emailResults,
      ...invoiceResults,
      ...packageResults,
      ...feedbackResults,
    ];
  }, [
    bookingResults,
    contactResults,
    emailResults,
    faqResults,
    feedbackResults,
    invoiceResults,
    normalizedQuery,
    packageResults,
    projectResults,
    routeResults,
    scopeMatches,
  ]);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      const pressedShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      if (!pressedShortcut) {
        return;
      }

      event.preventDefault();

      if (modal.isOpen) {
        modal.close();
        return;
      }

      modal.open();
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [modal]);

  useEffect(() => {
    if (!modal.isOpen) {
      return;
    }

    dispatch(fetchContacts(undefined));
    dispatch(fetchProjects());
    dispatch(fetchBookings(undefined));
    dispatch(fetchConversations(undefined));
    dispatch(fetchScheduledEmailsThunk());
    dispatch(fetchInvoices(undefined));
    dispatch(fetchPackages(undefined));
    dispatch(fetchSupportFeedback(undefined));
  }, [dispatch, modal.isOpen]);

  useEffect(() => {
    if (!modal.isOpen) {
      setQuery('');
      setScopeOverride(payloadScope);
      setSelectedIndex(0);
      return;
    }

    setScopeOverride(payloadScope);
    setSelectedIndex(0);
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [modal.isOpen, payloadScope]);

  useEffect(() => {
    if (selectedIndex <= filteredItems.length - 1) {
      return;
    }

    setSelectedIndex(Math.max(filteredItems.length - 1, 0));
  }, [filteredItems.length, selectedIndex]);

  const openItem = (href: string) => {
    navigate(href);
    modal.close();
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex(index =>
        filteredItems.length === 0 ? 0 : (index + 1) % filteredItems.length
      );
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex(index =>
        filteredItems.length === 0 ? 0 : (index - 1 + filteredItems.length) % filteredItems.length
      );
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const selectedItem = filteredItems[selectedIndex];
      if (selectedItem) {
        if (selectedItem.action) {
          selectedItem.action();
          return;
        }

        openItem(selectedItem.route);
      }
    }
  };

  return (
    <StandardModal
      isOpen={modal.isOpen}
      onClose={modal.close}
      title="Global Search"
      size="3xl"
      bodyClassName="p-0"
      headerActions={
        <div className="hidden sm:flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-500">
          {getGlobalSearchShortcutLabel()}
        </div>
      }
    >
      <div className="border-b border-gray-200 px-6 py-4">
        <label htmlFor="global-search-input" className="sr-only">
          Search pages and tools
        </label>
        <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 transition-shadow focus-within:border-primary-300 focus-within:ring-2 focus-within:ring-primary-100">
          <Search size={18} className="text-gray-400" />
          <input
            ref={inputRef}
            id="global-search-input"
            type="text"
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder={
              activeScopes.length === 1
                ? `Search ${GLOBAL_SEARCH_SCOPE_LABELS[activeScopes[0]].toLowerCase()}...`
                : 'Search pages, settings, and tools…'
            }
            className="w-full border-0 bg-transparent p-0 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
          />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {activeScopes.map(scope => (
            <button
              key={scope}
              type="button"
              onClick={() => setScopeOverride(null)}
              className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700"
            >
              {GLOBAL_SEARCH_SCOPE_LABELS[scope]}
              <X size={12} />
            </button>
          ))}
          {GLOBAL_SEARCH_SCOPES.map(scope => {
            const active = activeScopes.includes(scope);
            return (
              <button
                key={scope}
                type="button"
                onClick={() => {
                  setScopeOverride(scope);
                  setQuery('');
                }}
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  active ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {GLOBAL_SEARCH_SCOPE_LABELS[scope]}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-h-[60vh] overflow-y-auto p-3">
        {filteredItems.length > 0 ? (
          <div className="space-y-1">
            {filteredItems.map((item, index) => {
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    if (item.action) {
                      item.action();
                      return;
                    }

                    openItem(item.route);
                  }}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition-colors',
                    index === selectedIndex ? 'bg-primary-50' : 'hover:bg-gray-50'
                  )}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white text-gray-600 shadow-sm ring-1 ring-gray-200">
                    <item.icon size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-gray-900">
                        {item.title}
                      </span>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-gray-500">
                        {item.section}
                      </span>
                      {item.badge && (
                        <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-500 ring-1 ring-gray-200">
                          {item.badge}
                        </span>
                      )}
                      {item.current && (
                        <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                          Current
                        </span>
                      )}
                    </div>
                    <p className="truncate text-sm text-gray-500">{item.description}</p>
                  </div>
                  <ArrowRight size={16} className="shrink-0 text-gray-400" />
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400">
              <Search size={20} />
            </div>
            <h3 className="text-sm font-semibold text-gray-900">No matches found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try searching by page name, category, or related keyword.
            </p>
          </div>
        )}
      </div>
    </StandardModal>
  );
};

export default GlobalSearchModal;
